import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  useNavigate,
  useParams,
  useLocation,
  Link as RouterLink,
} from 'react-router-dom';
import {
  Armchair,
  ArrowLeft,
  Bath,
  BedDouble,
  ExternalLink,
  MapPin,
  Pencil,
  Save,
  X,
} from '../ui/icons';
import {
  PROPERTY_DEPARTMENT_LABELS,
  PROPERTY_FIELDS,
} from '../../models/property';
import {
  useProperty,
  useUpdateProperty,
} from '../../services/hooks/useProperties';
import { useSource } from '../../services/hooks/useSources';
import { UrlItemCard } from '../UrlItemCard';
import api from '../../services/api';
import {
  Button,
  Badge,
  Spinner,
  Alert,
  Card,
  CardContent,
  Input,
  Tabs,
} from '../ui';

const getDepartmentChipLabel = (department?: string) => {
  switch (department) {
    case 'residential-sales':
      return 'For Sale';
    case 'residential-lettings':
      return 'To Let';
    case 'commercial':
      return 'Commercial';
    default:
      return department
        ? PROPERTY_DEPARTMENT_LABELS[department] || department
        : 'Unknown';
  }
};

const MEDIA_FIELD_IDS = new Set([
  'photos',
  'floorplans',
  'brochures',
  'virtualTours',
  'epcs',
]);
const LOCATION_FIELD_IDS = new Set(['latitude', 'longitude', 'location']);
const DESCRIPTION_FIELD_IDS = new Set(['summary', 'features']);
const OVERVIEW_FIELD_IDS = new Set([
  'price',
  'priceQualifier',
  'availability',
  'bedrooms',
  'bathrooms',
  'receptionRooms',
  'location',
  'latitude',
  'longitude',
]);

const EXCLUDED_FIELD_IDS = new Set([
  ...Array.from(MEDIA_FIELD_IDS),
  ...Array.from(LOCATION_FIELD_IDS),
  ...Array.from(DESCRIPTION_FIELD_IDS),
  'title',
  ...Array.from(OVERVIEW_FIELD_IDS),
]);

const DETAILS_TABS = [
  { label: 'Overview' },
  { label: 'Description' },
  { label: 'Additional Fields' },
];

const hasFieldValue = (value: any) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const formatCurrency = (value?: number, currencyCode: string = 'GBP') => {
  if (!value && value !== 0) return 'N/A';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const resolveFieldGroup = (fieldId: string) => {
  if (fieldId.startsWith('address')) return 'Address';
  if (
    fieldId.includes('room') ||
    fieldId.includes('feature') ||
    fieldId.includes('description')
  ) {
    return 'Description & Features';
  }
  if (fieldId.includes('price') || fieldId.includes('rent')) {
    return 'Pricing';
  }
  if (fieldId.includes('available') || fieldId.includes('market')) {
    return 'Marketing';
  }
  return 'Other Details';
};

const getFieldById = (fieldId: string) =>
  PROPERTY_FIELDS.find((field) => field.id === fieldId);

const PropertyDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, sourceId } = useParams<{ id: string; sourceId: string }>();
  const backLocation = (
    location.state as { from?: { pathname: string; search?: string } }
  )?.from;
  const [activeImage, setActiveImage] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState<Record<string, any>>({});
  const [activeDetailsTab, setActiveDetailsTab] = useState(0);

  useLayoutEffect(() => {
    document.scrollingElement?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [id, location.pathname]);

  const {
    data: propertyResponse,
    isLoading: propertyLoading,
    error: propertyError,
    refetch: refetchProperty,
  } = useProperty(id || '', sourceId || '');

  const updatePropertyMutation = useUpdateProperty();
  const { data: source, isLoading: sourceLoading } = useSource(
    propertyResponse?.property?.sourceId || ''
  );

  const property = propertyResponse?.property;
  const urlItem = propertyResponse?.urlItem;
  const auditLogs = propertyResponse?.auditLogs || [];
  const isLoading = propertyLoading || sourceLoading;

  const handleUrlAction = async (
    action: 'process' | 'debug' | 'disable' | 'enable'
  ) => {
    if (!property || !urlItem) return;
    const propertySourceId = property.sourceId;
    const urlHash = urlItem.sk.replace('URL#', '');

    try {
      switch (action) {
        case 'process':
          await api.post(
            `/sources/${propertySourceId}/urls/${urlHash}/process`
          );
          break;
        case 'debug':
          const testResponse = await api.post(
            `/sources/${propertySourceId}/urls/test`,
            {
              sourceUrl: property.sourceUrl,
            }
          );
          const testResult = testResponse.data;
          const newWindow = window.open('', '_blank');
          if (newWindow) {
            newWindow.document.write(`
              <html>
                <head><title>URL Debug Results</title></head>
                <body>
                  <h1>Debug Results for: ${property.sourceUrl}</h1>
                  <pre>${JSON.stringify(testResult, null, 2)}</pre>
                </body>
              </html>
            `);
          }
          break;
        case 'disable':
        case 'enable':
          await api.put(`/sources/${propertySourceId}/urls/${urlHash}`, {
            active: action === 'enable',
          });
          break;
      }
      await refetchProperty();
    } catch (error) {
      console.error(`Error performing ${action} on URL:`, error);
    }
  };

  const handleSave = async () => {
    if (!property || !id || !sourceId) return;
    try {
      await updatePropertyMutation.mutateAsync({
        id,
        sourceId,
        updates: editedFields,
      });
      setIsEditing(false);
      setEditedFields({});
    } catch (error) {
      console.error('Error updating property:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedFields({});
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setEditedFields((prev) => ({ ...prev, [fieldId]: value }));
  };

  const getCurrentFieldValue = (fieldId: string) => {
    if (Object.prototype.hasOwnProperty.call(editedFields, fieldId)) {
      return editedFields[fieldId];
    }
    return property?.fields[fieldId as keyof typeof property.fields];
  };

  const renderEditableInput = (
    fieldId: string,
    value: any,
    options: {
      type?: 'text' | 'number';
      multiline?: boolean;
      displayValue?: React.ReactNode;
    } = {}
  ) => {
    if (isEditing) {
      const normalizedValue = value ?? '';

      if (options.multiline) {
        return (
          <textarea
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={normalizedValue}
            onChange={(event) => handleFieldChange(fieldId, event.target.value)}
          />
        );
      }

      return (
        <Input
          fullWidth
          type={options.type === 'number' ? 'number' : 'text'}
          value={normalizedValue}
          onChange={(event) => {
            const nextValue =
              options.type === 'number'
                ? event.target.value === ''
                  ? ''
                  : Number(event.target.value)
                : event.target.value;
            handleFieldChange(fieldId, nextValue);
          }}
        />
      );
    }

    if (options.displayValue !== undefined && options.displayValue !== null) {
      return options.displayValue;
    }

    if (!hasFieldValue(value)) {
      return <span className="text-gray-500">Not specified</span>;
    }

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    return value;
  };

  const renderFieldValue = (
    field: (typeof PROPERTY_FIELDS)[number],
    value: any
  ) => {
    if (isEditing) {
      const normalizedValue = Array.isArray(value)
        ? value.join('\n')
        : value ?? '';

      if (field.type === 'valueList' && field.values) {
        return (
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={normalizedValue}
            onChange={(event) =>
              handleFieldChange(field.id, event.target.value)
            }
          >
            <option value="">Select an option</option>
            {Object.entries(field.values).map(([optionValue, label]) => (
              <option key={optionValue} value={optionValue}>
                {label}
              </option>
            ))}
          </select>
        );
      }

      if (field.multiple || Array.isArray(value)) {
        return (
          <textarea
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={normalizedValue}
            onChange={(event) => {
              const lines = event.target.value
                .split('\n')
                .map((entry) => entry.trim())
                .filter(Boolean);
              handleFieldChange(field.id, lines);
            }}
          />
        );
      }

      const inputType = field.type === 'number' ? 'number' : 'text';
      return (
        <Input
          fullWidth
          type={inputType}
          value={normalizedValue}
          onChange={(event) => {
            if (inputType === 'number') {
              handleFieldChange(
                field.id,
                event.target.value === '' ? '' : Number(event.target.value)
              );
            } else {
              handleFieldChange(field.id, event.target.value);
            }
          }}
        />
      );
    }

    if (!hasFieldValue(value)) {
      return <span className="text-gray-500">Not specified</span>;
    }

    if (
      field.type === 'valueList' &&
      field.values &&
      typeof value === 'string'
    ) {
      return field.values[value] || value;
    }

    if (Array.isArray(value)) {
      return (
        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-900">
          {value.map((entry, index) => (
            <li key={`${field.id}-${index}`}>
              {typeof entry === 'string' ? entry : JSON.stringify(entry)}
            </li>
          ))}
        </ul>
      );
    }

    if (typeof value === 'object') {
      return (
        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs overflow-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    return value;
  };

  const addressText = useMemo(() => {
    const addressFields = [
      getCurrentFieldValue('addressNameNumber'),
      getCurrentFieldValue('addressStreet'),
      getCurrentFieldValue('addressTwo'),
      getCurrentFieldValue('addressThree'),
      getCurrentFieldValue('addressFour'),
      getCurrentFieldValue('city'),
      getCurrentFieldValue('postcode'),
      getCurrentFieldValue('country'),
    ];

    return addressFields
      .map((part) => (typeof part === 'string' ? part.trim() : ''))
      .filter(Boolean)
      .join(', ');
  }, [property, editedFields]);

  const currency =
    (getCurrentFieldValue('currency') as string) ||
    property?.fields.currency ||
    'GBP';
  const priceValue = getCurrentFieldValue('price');
  const priceDisplayValue = hasFieldValue(priceValue)
    ? formatCurrency(Number(priceValue), currency)
    : undefined;
  const priceQualifierValue = getCurrentFieldValue('priceQualifier');
  const locationValue = getCurrentFieldValue('location');
  const primaryLocationText =
    typeof locationValue === 'string'
      ? locationValue.trim()
      : locationValue
      ? String(locationValue).trim()
      : undefined;

  const latitudeRaw = getCurrentFieldValue('latitude');
  const longitudeRaw = getCurrentFieldValue('longitude');
  const latitude =
    typeof latitudeRaw === 'number'
      ? latitudeRaw
      : latitudeRaw
      ? Number(latitudeRaw)
      : undefined;
  const longitude =
    typeof longitudeRaw === 'number'
      ? longitudeRaw
      : longitudeRaw
      ? Number(longitudeRaw)
      : undefined;
  const hasCoordinates =
    Number.isFinite(latitude) && Number.isFinite(longitude);

  const getFieldDisplayValue = (fieldId: string) => {
    const field = getFieldById(fieldId);
    if (!field) return undefined;
    return renderFieldValue(field, getCurrentFieldValue(fieldId));
  };

  const photos = (getCurrentFieldValue('photos') as string[]) || [];
  const summaryValue = getCurrentFieldValue('summary');
  const featuresValue = (getCurrentFieldValue('features') as string[]) || [];
  const trimmedFeatures = featuresValue
    .map((feature) => {
      if (typeof feature === 'string') return feature.trim();
      if (feature === undefined || feature === null) return '';
      return String(feature).trim();
    })
    .filter(Boolean);

  const mediaSections = useMemo(
    () => [
      {
        label: 'Floorplans',
        items: (getCurrentFieldValue('floorplans') as string[]) || [],
      },
      {
        label: 'Brochures',
        items: (getCurrentFieldValue('brochures') as string[]) || [],
      },
      {
        label: 'EPCs',
        items: (getCurrentFieldValue('epcs') as string[]) || [],
      },
      {
        label: 'Virtual Tours',
        type: 'virtual',
        items: (getCurrentFieldValue('virtualTours') as string[]) || [],
      },
    ],
    [property, editedFields]
  );

  const locationDisplayText = primaryLocationText || addressText;
  const showAddressLine =
    Boolean(primaryLocationText) &&
    Boolean(addressText) &&
    primaryLocationText !== addressText;

  const fieldGroups = useMemo(() => {
    if (!property)
      return {} as Record<string, (typeof PROPERTY_FIELDS)[number][]>;

    const groups: Record<string, (typeof PROPERTY_FIELDS)[number][]> = {};

    PROPERTY_FIELDS.forEach((field) => {
      if (EXCLUDED_FIELD_IDS.has(field.id)) return;

      const groupName = resolveFieldGroup(field.id);
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(field);
    });

    return groups;
  }, [property, editedFields]);

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (propertyError || !property) {
    return (
      <div className="p-6">
        <Alert variant="error">Unable to load property details.</Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <button
          onClick={() =>
            backLocation
              ? navigate(`${backLocation.pathname}${backLocation.search || ''}`)
              : navigate('/properties')
          }
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            {isEditing ? (
              <Input
                fullWidth
                value={
                  (getCurrentFieldValue('title') as string) ??
                  property.title ??
                  ''
                }
                onChange={(event) =>
                  handleFieldChange('title', event.target.value)
                }
              />
            ) : (
              <h1 className="text-2xl font-semibold truncate">
                {property.title || 'Untitled Property'}
              </h1>
            )}
          </div>
        </div>
        {isEditing ? (
          <>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={updatePropertyMutation.isPending}
              className="mr-2"
            >
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button variant="outline" onClick={handleCancel} className="mr-2">
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            onClick={() => setIsEditing(true)}
            className="mr-2"
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        )}
        {property.sourceUrl && (
          <Button
            variant="outline"
            onClick={() => window.open(property.sourceUrl, '_blank')}
          >
            View Original
            <ExternalLink className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-7 space-y-6">
          <Card>
            <CardContent>
              <div className="flex items-center justify-between mb-4 gap-4">
                <h2 className="text-xl font-semibold">Property Details</h2>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={
                      property.gsi1pk === 'PROPERTY#ACTIVE'
                        ? 'success'
                        : 'default'
                    }
                    size="sm"
                  >
                    {property.gsi1pk === 'PROPERTY#ACTIVE'
                      ? 'Active'
                      : 'Inactive'}
                  </Badge>
                  {property.locked && (
                    <Badge variant="warning" size="sm">
                      Locked
                    </Badge>
                  )}
                </div>
              </div>
              <Tabs
                tabs={DETAILS_TABS}
                activeTab={activeDetailsTab}
                onChange={setActiveDetailsTab}
                className="mb-4"
              />
              {activeDetailsTab === 0 && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs uppercase text-gray-500 mb-1">
                        Price
                      </p>
                      <div className="text-base text-gray-900">
                        {renderEditableInput('price', priceValue, {
                          type: 'number',
                          displayValue: priceDisplayValue,
                        })}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs uppercase text-gray-500 mb-1">
                        Price Qualifier
                      </p>
                      <div className="text-base text-gray-900">
                        {renderEditableInput(
                          'priceQualifier',
                          priceQualifierValue,
                          {
                            displayValue:
                              getFieldDisplayValue('priceQualifier'),
                          }
                        )}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs uppercase text-gray-500 mb-1">
                        Availability
                      </p>
                      <div className="text-base text-gray-900">
                        {renderEditableInput(
                          'availability',
                          getCurrentFieldValue('availability'),
                          {
                            displayValue: getFieldDisplayValue('availability'),
                          }
                        )}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs uppercase text-gray-500 mb-1">
                        Beds
                      </p>
                      <div className="text-base text-gray-900">
                        {renderEditableInput(
                          'bedrooms',
                          getCurrentFieldValue('bedrooms'),
                          {
                            type: 'number',
                            displayValue: getFieldDisplayValue('bedrooms'),
                          }
                        )}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs uppercase text-gray-500 mb-1">
                        Baths
                      </p>
                      <div className="text-base text-gray-900">
                        {renderEditableInput(
                          'bathrooms',
                          getCurrentFieldValue('bathrooms'),
                          {
                            type: 'number',
                            displayValue: getFieldDisplayValue('bathrooms'),
                          }
                        )}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs uppercase text-gray-500 mb-1">
                        Reception
                      </p>
                      <div className="text-base text-gray-900">
                        {renderEditableInput(
                          'receptionRooms',
                          getCurrentFieldValue('receptionRooms'),
                          {
                            type: 'number',
                            displayValue:
                              getFieldDisplayValue('receptionRooms'),
                          }
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`mt-6 flex items-start gap-3 ${
                      hasCoordinates ? 'text-green-700' : 'text-gray-600'
                    }`}
                  >
                    <MapPin
                      className={`${
                        hasCoordinates ? 'text-green-600' : 'text-gray-400'
                      } mt-1 h-4 w-4`}
                    />
                    <div className="flex-1">
                      <p className="text-sm text-gray-800">
                        {locationDisplayText || 'No address available'}
                      </p>
                      {showAddressLine && (
                        <p className="text-xs text-gray-500 mt-1">
                          {addressText}
                        </p>
                      )}
                      {hasCoordinates && (
                        <p className="text-xs text-gray-500 mt-1">
                          Lat: {latitude}, Lng: {longitude}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
              {activeDetailsTab === 1 && (
                <div className="mt-6 overflow-hidden">
                  {trimmedFeatures.length > 0 && (
                    <div className="float-right w-full max-w-xs lg:w-72 ml-4 mb-4 bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-900">
                      <p className="text-xs uppercase text-gray-500 mb-2">
                        Features
                      </p>
                      <ul className="list-disc pl-5 space-y-1">
                        {trimmedFeatures.map((feature, index) => (
                          <li key={`${feature}-${index}`}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="text-sm text-gray-900 break-words">
                    {isEditing ? (
                      renderEditableInput('summary', summaryValue, {
                        multiline: true,
                      })
                    ) : summaryValue ? (
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: summaryValue as string,
                        }}
                      />
                    ) : (
                      <p className="text-gray-500">No summary provided</p>
                    )}
                  </div>
                </div>
              )}
              {activeDetailsTab === 2 && (
                <div className="space-y-6 mt-4">
                  {Object.entries(fieldGroups).length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No additional fields available.
                    </p>
                  ) : (
                    Object.entries(fieldGroups).map(([groupName, fields]) => (
                      <div key={groupName}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                          {groupName}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {fields.map((field) => (
                            <div
                              key={field.id}
                              className="bg-gray-50 rounded-lg p-4"
                            >
                              <p className="text-xs uppercase text-gray-500 mb-1">
                                {field.name}
                              </p>
                              <div className="text-sm text-gray-900 break-words">
                                {renderFieldValue(
                                  field,
                                  getCurrentFieldValue(field.id)
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <h2 className="text-xl font-semibold mb-4">Property History</h2>
              {auditLogs.length === 0 ? (
                <p className="text-gray-500">No history available</p>
              ) : (
                <div className="space-y-4">
                  {auditLogs.map((log, index) => (
                    <div
                      key={index}
                      className="border border-gray-100 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                        <Badge variant="primary" size="sm">
                          {log.logType}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-800">{log.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="xl:col-span-5 space-y-6">
          <Card>
            <CardContent>
              <h2 className="text-xl font-semibold mb-4">Media</h2>
              {photos.length > 0 ? (
                <>
                  <div className="bg-black rounded-lg overflow-hidden">
                    <img
                      src={photos[activeImage]}
                      alt={`Property image ${activeImage + 1}`}
                      className="w-full h-80 object-contain"
                    />
                  </div>
                  {photos.length > 1 && (
                    <div className="flex overflow-x-auto mt-2 gap-2 pb-2">
                      {photos.map((photo, index) => (
                        <button
                          type="button"
                          key={`${photo}-${index}`}
                          className={`w-20 h-16 border-2 rounded overflow-hidden flex-none ${
                            index === activeImage
                              ? 'border-primary-600'
                              : 'border-transparent'
                          }`}
                          onClick={() => setActiveImage(index)}
                        >
                          <img
                            src={photo}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="h-64 flex items-center justify-center bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-500">No images available</p>
                </div>
              )}

              {mediaSections.map(
                (section) =>
                  section.items.length > 0 && (
                    <div key={section.label} className="mt-6">
                      <h3 className="text-lg font-semibold mb-2">
                        {section.label}
                      </h3>
                      <div className="flex flex-col gap-2 text-sm">
                        {section.items.map((item: any, index: number) => {
                          if (section.type === 'virtual') {
                            const url = item as string;
                            return (
                              <a
                                key={`${section.label}-${index}`}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block text-primary-600 hover:underline"
                              >
                                Virtual tour {index + 1}
                              </a>
                            );
                          }
                          return (
                            <a
                              key={`${section.label}-${index}`}
                              href={item}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-primary-600 hover:underline"
                            >
                              {section.label} {index + 1}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <h2 className="text-xl font-semibold mb-4">Record Details</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-xs uppercase text-gray-500 mb-1">
                    Property ID
                  </p>
                  <p className="text-sm text-gray-900">{property.id}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500 mb-1">Source</p>
                  <RouterLink
                    to={`/sources/${property.sourceId}`}
                    className="text-sm text-primary-600 hover:underline"
                  >
                    {source ? source.name : property.sourceId}
                  </RouterLink>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500 mb-1">
                    Agency ID
                  </p>
                  <p className="text-sm text-gray-900">
                    {property.agencyId || 'Not specified'}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500 mb-1">Added</p>
                  <p className="text-sm text-gray-900">
                    {new Date(property.createdAt).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500 mb-1">
                    Last Updated
                  </p>
                  <p className="text-sm text-gray-900">
                    {new Date(property.updatedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {urlItem ? (
            <UrlItemCard
              urlItem={urlItem}
              sourceId={sourceId || ''}
              onUrlAction={handleUrlAction}
            />
          ) : (
            <Card>
              <CardContent>
                <h3 className="text-lg font-semibold mb-2">URL Information</h3>
                <p className="text-sm text-gray-500">
                  No URL item found for this property.
                </p>
                {property.sourceUrl && (
                  <p className="text-sm mt-2 break-all">{property.sourceUrl}</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
